import { Link, useNavigate, useMatch } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useState, useRef, useEffect } from 'react';
import type { DateRange } from '../types';
import { LoadingBar } from './LoadingBar';

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function usePageTitle(): { title: string; showBack: boolean; trail?: { label: string; to: string } } {
  const matchRuns     = useMatch('/runs');
  const matchDetail   = useMatch('/runs/:build');
  const matchTcDetail = useMatch('/test-cases/:build/:id');
  const matchExplorer = useMatch('/test-cases');
  const matchBugs     = useMatch('/bugs');
  const matchNewRun   = useMatch('/new-run');

  if (matchRuns)     return { title: 'Dashboard', showBack: false };
  if (matchDetail)   return { title: `Build #${(matchDetail.params.build ?? '').split('-')[0]}`, showBack: true, trail: { label: 'Runs', to: '/runs' } };
  if (matchTcDetail) return { title: `Test case ${matchTcDetail.params.id ?? ''}`, showBack: true, trail: { label: `Build #${(matchTcDetail.params.build ?? '').split('-')[0]}`, to: `/runs/${matchTcDetail.params.build ?? ''}` } };
  if (matchExplorer) return { title: 'Test cases', showBack: false };
  if (matchBugs)     return { title: 'Bugs', showBack: false };
  if (matchNewRun)   return { title: 'Trigger run', showBack: false };
  return { title: 'AI Exploratory Testing', showBack: false };
}

const DATE_OPTIONS: { label: string; value: DateRange }[] = [
  { label: 'This month', value: 'month' },
  { label: 'Last 7d',    value: '7' },
  { label: 'Last 30d',   value: '30' },
  { label: 'All time',   value: '' },
  { label: 'Custom',     value: 'custom' },
];

export function Topbar() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { dateRange, dateFrom, dateTo, setDateRange, setDateFrom, setDateTo, label } = useDateFilter();
  const { title, showBack, trail } = usePageTitle();
  const hideDate = !!useMatch('/new-run');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 'var(--sb-w, 220px)',
        right: 0,
        height: '56px',
        background: 'var(--c-header)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid var(--c-b2)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 32px',
        zIndex: 30,
        gap: '12px',
      }}
    >
      {/* Back button */}
      {showBack && (
        <button
          onClick={() => navigate(-1)}
          style={{
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--c-b1)',
            border: '1px solid var(--c-b2)',
            borderRadius: '7px',
            cursor: 'pointer',
            color: 'var(--c-t3)',
            flexShrink: 0,
          }}
        >
          <ChevronLeftIcon />
        </button>
      )}

      {/* Title */}
      {/* Top-level pages: the active sidebar item already names the page, so only detail pages get a title. */}
      <nav aria-label="Breadcrumb" className="crumbs" style={{ flex: 1 }}>
        {showBack && trail && <><Link to={trail.to}>{trail.label}</Link><span aria-hidden>›</span></>}
        {showBack && <span className="crumb-cur" aria-current="page">{title}</span>}
      </nav>

      <button type="button" className="cmdk-pill" onClick={() => window.dispatchEvent(new Event('expo:palette'))} aria-label="Open command palette">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        Search<kbd>⌘K</kbd>
      </button>

      {/* Global date filter */}
      <div ref={ref} style={{ position: 'relative', display: hideDate ? 'none' : undefined }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '5px 12px',
            fontSize: '12.5px', fontWeight: 500,
            color: open ? 'var(--accent)' : 'var(--c-t3)',
            background: open ? 'var(--ca-bg)' : 'var(--c-b1)',
            border: `1px solid ${open ? 'rgb(var(--accent-rgb) / 0.35)' : 'var(--c-b2)'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <CalendarIcon />
          {label}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              minWidth: '180px',
              background: 'var(--c-overlay)',
              border: '1px solid var(--c-b4)',
              borderRadius: '8px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              overflow: 'hidden',
              zIndex: 100,
            }}
          >
            {DATE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  setDateRange(opt.value);
                  if (opt.value !== 'custom') setOpen(false);
                }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: dateRange === opt.value ? 600 : 400,
                  color: dateRange === opt.value ? 'var(--accent)' : 'var(--c-t0b)',
                  background: dateRange === opt.value ? 'var(--ca-bg2)' : 'transparent',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
            {dateRange === 'custom' && (
              <div style={{ padding: '8px 14px', borderTop: '1px solid var(--c-b1)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input
                  aria-label="From date"
                  type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  style={{
                    background: 'var(--c-b1)', border: '1px solid var(--c-b4)', borderRadius: '5px',
                    color: 'var(--c-t0b)', fontSize: '12px', padding: '4px 8px', width: '100%',
                  }}
                />
                <input
                  aria-label="To date"
                  type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  style={{
                    background: 'var(--c-b1)', border: '1px solid var(--c-b4)', borderRadius: '5px',
                    color: 'var(--c-t0b)', fontSize: '12px', padding: '4px 8px', width: '100%',
                  }}
                />
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    padding: '5px', background: 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: '5px', fontSize: '12px',
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        title={`Theme: ${theme}. Click for ${theme === 'dark' ? 'light' : 'dark'}`}
        aria-label={`Theme: ${theme}. Switch theme`}
        style={{
          width: 36, height: 36,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--c-b1)',
          border: '1px solid var(--c-b2)',
          borderRadius: '8px',
          cursor: 'pointer',
          color: 'var(--c-t3)',
          flexShrink: 0,
        }}
      >
        {theme === 'light' ? <SunIcon /> : <MoonIcon />}
      </button>

      <LoadingBar />
    </header>
  );
}
